<template>
  <div :class="['flex h-full flex-col overflow-hidden', isDesktop && 'w-full max-w-[700px]']">
    <!-- MOBILE LAYOUT -->
    <template v-if="!isDesktop">
      <div class="flex h-full flex-col overflow-hidden bg-white">
        <div class="min-h-0 flex-1 overflow-y-auto p-2">
          <!-- Empty state -->
          <div
            v-if="panel.pins.length === 0"
            class="flex h-full flex-col items-center justify-center py-4 text-center text-gray-500"
          >
            <PinMarkerIcon class="mb-3 h-12 w-12 -rotate-45 text-gray-300" />
            <p class="m-0 max-w-[280px] text-sm leading-relaxed">
              No instructions added yet. Click anywhere on the image to drop a pin and add
              instructions for our artists.
            </p>
          </div>

          <!-- Detail view for active pin (mobile) -->
          <div
            v-else-if="panel.mobileView === 'detail' && panel.activePin"
            :class="[
              'flex flex-col overflow-hidden rounded-lg border border-blue-200 bg-blue-50/30',
              isActiveChat && 'h-full'
            ]"
          >
            <!-- Detail header -->
            <div
              class="flex shrink-0 items-center justify-between border-b border-blue-200 px-2 py-1.5"
            >
              <div class="flex items-center gap-1 font-medium">
                <button
                  v-if="isCompletedPin(panel.activePin)"
                  type="button"
                  class="flex cursor-pointer rounded border-none bg-transparent p-0.5 text-gray-700 hover:bg-gray-100"
                  data-testid="pin-back-button"
                  @click="goBackToList"
                >
                  <UseSvg icon="arrow-left" class="h-3.5 w-3.5" />
                </button>
                <span class="text-sm font-semibold text-gray-900">
                  <span :style="{ color: PIN_COLORS[activePinIndex % PIN_COLORS.length] }">
                    Pin {{ activePinIndex + 1 }} </span
                  >:
                  <span class="font-normal text-gray-500">What would you like to do?</span>
                </span>
              </div>
              <div class="flex items-center gap-1">
                <button
                  v-if="isActiveChat"
                  type="button"
                  class="flex cursor-pointer items-center rounded border-none bg-transparent p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  data-testid="pin-panel-expand-toggle"
                  :aria-label="panel.isPanelExpanded ? 'Collapse panel' : 'Expand panel'"
                  @click="panel.isPanelExpanded = !panel.isPanelExpanded"
                >
                  <UseSvg v-if="panel.isPanelExpanded" icon="chevron-down" class="h-4 w-4" />
                  <ChevronUpIcon v-else class="h-4 w-4" />
                </button>
                <button
                  type="button"
                  class="flex cursor-pointer items-center rounded border-none bg-transparent p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                  :data-testid="`pin-delete-${panel.activePin.id}`"
                  :aria-label="`Delete pin ${panel.activePin.id}`"
                  :disabled="panel.removingPinIds.has(panel.activePin.id)"
                  @click="handleDeleteClick(panel.activePin)"
                >
                  <UseSvg
                    v-if="panel.removingPinIds.has(panel.activePin.id)"
                    icon="loader"
                    class="h-3.5 w-3.5 animate-spin"
                  />
                  <UseSvg v-else icon="trash" class="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <!-- Messages area -->
            <div ref="messagesScrollRef" class="min-h-0 flex-1 overflow-y-auto">
              <div
                v-if="
                  panel.activePin.messages.length > 0 ||
                  (panel.activePin.state === 'ready' && panel.activePin.instruction)
                "
                class="px-2 py-1.5"
              >
                <PinReadyCard
                  v-if="panel.activePin.state === 'ready' && panel.activePin.instruction"
                  :instruction="panel.activePin.instruction"
                  compact
                />
                <PinMessages
                  v-else
                  :messages="panel.activePin.messages"
                  :is-processing="panel.activePin.state === 'processing'"
                  :thinking-text="panel.activePin.thinkingText"
                  compact
                />
              </div>
              <div
                v-if="
                  panel.activePin.state === 'needs_clarification' &&
                  panel.pendingPillClarifications.length > 0
                "
                class="px-2 pb-2"
              >
                <PinClarifications
                  :clarifications="panel.pendingPillClarifications"
                  :responses="panel.clarificationResponses"
                  :is-disabled="false"
                  :text-info="panel.lastAiMessageWithClarifications?.textInfo"
                  :selected-font="panel.selectedFontValue"
                  :selected-color="panel.selectedColorValue"
                  :unanswered-above="panel.unansweredAbove"
                  :unanswered-below="panel.unansweredBelow"
                  compact
                  @select-option="(q, v) => panel.handleSelectClarificationOption(q, v)"
                  @toggle-custom-input="q => panel.handleToggleCustomInput(q)"
                  @custom-input-change="(q, v) => panel.handleCustomInputChange(q, v)"
                  @submit="panel.handleSubmit()"
                  @scroll="panel.calculateUnansweredPositions()"
                  @scroll-to-unanswered="dir => panel.scrollToFirstUnanswered(dir)"
                />
              </div>
            </div>

            <!-- Composer -->
            <div
              v-if="panel.activePin.state !== 'ready'"
              class="shrink-0 border-t border-blue-200 p-2"
            >
              <PinComposer
                :model-value="panel.composerText"
                :can-submit="
                  panel.hasAnyClarificationResponse || panel.isComposerTextValid(panel.composerText)
                "
                :is-processing="panel.activePin.state === 'processing'"
                :is-removing="panel.removingPinIds.has(panel.activePin.id)"
                compact
                @update:model-value="
                  val => {
                    panel.composerText = val;
                  }
                "
                @submit="panel.handleSubmit()"
                @cancel="panel.handleDeletePin(panel.activePin.id)"
                @newline="
                  pos => {
                    panel.pendingCursorPosition = pos;
                  }
                "
                @textarea-ref="
                  el => {
                    panel.textareaRef = el;
                  }
                "
              />
            </div>
          </div>

          <!-- List view (mobile) -->
          <div v-else class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
            <div
              v-for="(pin, index) in panel.pins"
              :key="pin.id"
              class="flex cursor-pointer items-start justify-between gap-2 rounded-lg border border-gray-200 bg-white p-2.5 transition-colors hover:bg-gray-50"
              @click="handlePinListClick(pin, index)"
            >
              <div class="min-w-0 flex-1">
                <span
                  class="text-xs font-semibold"
                  :style="{ color: PIN_COLORS[index % PIN_COLORS.length] }"
                >
                  Pin {{ index + 1 }}
                </span>
                <p
                  v-if="pin.instruction && pin.state === 'ready'"
                  class="mt-1 truncate text-sm text-gray-700"
                >
                  {{ pin.instruction }}
                </p>
                <p v-else class="mt-1 text-sm text-gray-500">
                  {{ isCompletedPin(pin) ? "Completed" : "What would you like to do?" }}
                </p>
              </div>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="flex cursor-pointer items-center rounded border-none bg-transparent p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                  :data-testid="`pin-delete-list-${pin.id}`"
                  :disabled="panel.removingPinIds.has(pin.id)"
                  @click.stop="handleDeleteClick(pin)"
                >
                  <UseSvg
                    v-if="panel.removingPinIds.has(pin.id)"
                    icon="loader"
                    class="h-4 w-4 animate-spin"
                  />
                  <UseSvg v-else icon="trash" class="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- DESKTOP LAYOUT -->
    <template v-else>
      <div ref="pinsContainerRef" class="flex-1 overflow-y-auto p-4">
        <!-- Empty state -->
        <div
          v-if="panel.pins.length === 0"
          class="flex h-full flex-col items-center justify-center text-center text-gray-500"
        >
          <PinMarkerIcon class="mb-4 h-16 w-16 -rotate-45 text-gray-300" />
          <p class="m-0 max-w-[280px] text-sm leading-relaxed">
            No instructions added yet. Click anywhere on the image to drop a pin and add
            instructions for our artists.
          </p>
        </div>

        <!-- Pin cards -->
        <div v-else class="flex flex-col gap-4 pb-4">
          <div
            v-for="(pin, index) in panel.pins"
            :key="pin.id"
            :data-pin-id="pin.id"
            :class="[
              'flex flex-col gap-3 rounded-lg border bg-white p-4 transition-colors',
              pin.id === panel.activePinId ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200'
            ]"
            @mouseenter="
              isCompletedPin(pin) &&
              ((panel.hoveredPinId = pin.id), (panel.hoveredPinSource = 'panel'))
            "
            @mouseleave="
              isCompletedPin(pin) && ((panel.hoveredPinId = null), (panel.hoveredPinSource = null))
            "
          >
            <!-- Card header -->
            <div class="flex items-center justify-between">
              <div
                class="flex cursor-pointer items-center gap-2 font-medium"
                @click="handleDesktopPinClick(pin)"
              >
                <span class="text-sm font-semibold text-gray-900">
                  <span :style="{ color: PIN_COLORS[index % PIN_COLORS.length] }">
                    Pin {{ index + 1 }} </span
                  >:
                  <span class="font-normal text-gray-500">What would you like to do?</span>
                </span>
              </div>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="flex cursor-pointer items-center rounded border-none bg-transparent p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                  :data-testid="`pin-delete-${pin.id}`"
                  :disabled="panel.removingPinIds.has(pin.id)"
                  @click="handleDeleteClick(pin)"
                >
                  <UseSvg
                    v-if="panel.removingPinIds.has(pin.id)"
                    icon="loader"
                    class="h-4 w-4 animate-spin"
                  />
                  <UseSvg v-else icon="trash" class="h-4 w-4" />
                </button>
              </div>
            </div>

            <!-- Card body: messages + ready card -->
            <div
              v-if="pin.messages.length > 0 || (pin.state === 'ready' && pin.instruction)"
              class="flex flex-col gap-4"
            >
              <PinReadyCard
                v-if="pin.state === 'ready' && pin.instruction"
                :instruction="pin.instruction"
              />
              <PinMessages
                v-else
                :messages="pin.messages"
                :is-processing="pin.state === 'processing'"
                :thinking-text="pin.thinkingText"
              />
            </div>

            <!-- Composer (only for active, non-ready pins) -->
            <div
              v-if="pin.id === panel.activePinId && pin.state !== 'ready'"
              :ref="
                el => {
                  if (el) pinComposerRef.value = el;
                }
              "
              :class="['scroll-mb-4', pin.messages.length > 0 && 'mt-2']"
            >
              <PinClarifications
                v-if="
                  pin.state === 'needs_clarification' && panel.pendingPillClarifications.length > 0
                "
                :clarifications="panel.pendingPillClarifications"
                :responses="panel.clarificationResponses"
                :is-disabled="pin.state === 'processing'"
                :text-info="panel.lastAiMessageWithClarifications?.textInfo"
                :selected-font="panel.selectedFontValue"
                :selected-color="panel.selectedColorValue"
                :unanswered-above="panel.unansweredAbove"
                :unanswered-below="panel.unansweredBelow"
                @select-option="(q, v) => panel.handleSelectClarificationOption(q, v)"
                @toggle-custom-input="q => panel.handleToggleCustomInput(q)"
                @custom-input-change="(q, v) => panel.handleCustomInputChange(q, v)"
                @submit="panel.handleSubmit()"
                @scroll="panel.calculateUnansweredPositions()"
                @scroll-to-unanswered="dir => panel.scrollToFirstUnanswered(dir)"
              />
              <PinComposer
                :model-value="panel.composerText"
                :can-submit="
                  panel.hasAnyClarificationResponse || panel.isComposerTextValid(panel.composerText)
                "
                :is-processing="pin.state === 'processing'"
                :is-removing="panel.removingPinIds.has(pin.id)"
                @update:model-value="
                  val => {
                    panel.composerText = val;
                  }
                "
                @submit="panel.handleSubmit()"
                @cancel="handleDeleteClick(pin)"
                @newline="
                  pos => {
                    panel.pendingCursorPosition = pos;
                  }
                "
                @textarea-ref="
                  el => {
                    panel.textareaRef = el;
                  }
                "
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import ChevronUpIcon from "assets/icons/chevron-up.svg?vue";
import PinMarkerIcon from "assets/icons/pin-marker.svg?vue";
import UseSvg from "common/vue/components/UseSvg.vue";
import { computed, inject, reactive, ref, watch } from "vue";
import PinClarifications from "./PinClarifications.vue";
import PinComposer from "./PinComposer.vue";
import PinMessages from "./PinMessages.vue";
import PinReadyCard from "./PinReadyCard.vue";
import { PIN_COLORS } from "./composables/usePinningPanel";

defineOptions({ name: "PinningPanel" });

defineProps({
  isDesktop: { type: Boolean, default: false }
});

const SCROLL_BEHAVIOR_SMOOTH = "smooth";
const SCROLL_BLOCK_NEAREST = "nearest";

const panel = reactive(inject("pinningPanel"));

const pinsContainerRef = ref(null);
const messagesScrollRef = ref(null);
const pinComposerRef = ref(null);

watch(
  [() => panel.activePinId, () => panel.activePin?.messages.length, () => panel.activePin?.state],
  () => {
    if (panel.activePinId && pinComposerRef.value) {
      pinComposerRef.value.scrollIntoView({
        behavior: SCROLL_BEHAVIOR_SMOOTH,
        block: SCROLL_BLOCK_NEAREST
      });
    }
  },
  { flush: "post" }
);

const activePinIndex = computed(() => {
  if (!panel.activePin) return -1;
  return panel.pins.findIndex(pin => pin.id === panel.activePin.id);
});

const isActiveChat = computed(
  () =>
    panel.activePin && panel.activePin.state !== "ready" && panel.activePin.state !== "unsupported"
);

function isCompletedPin(pin) {
  return pin.state === "ready" || pin.state === "unsupported";
}

function goBackToList() {
  panel.mobileView = "list";
  panel.activePinId = null;
}

function handlePinListClick(pin) {
  panel.trackPinSelected(pin.id, panel.activePinId, pin.state);
  panel.activePinId = pin.id;
  panel.mobileView = "detail";
}

function handleDesktopPinClick(pin) {
  if (
    panel.activePin &&
    panel.activePin.state !== "ready" &&
    panel.activePin.state !== "unsupported"
  ) {
    return;
  }
  if (pin.id !== panel.activePinId) {
    panel.trackPinSelected(pin.id, panel.activePinId, pin.state);
  }
  panel.activePinId = pin.id === panel.activePinId ? null : pin.id;
}

function handleDeleteClick(pin) {
  if (isCompletedPin(pin)) {
    panel.openDeletePinModal(pin.id);
  } else {
    panel.handleDeletePin(pin.id);
  }
}
</script>
